import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder } from '@angular/forms';
import { Store } from '@ngrx/store';
import {
  selectEventsHistory,
  selectEventsHistoryCounts,
} from '@app/main/main-screen/store/data/events/events.selectors';
import { DialogEventState } from '@app/main/leak-detection/dialogs/dialog-event/services/dialog-event-state.service';
import { DialogEventPersistenceService } from '@app/main/leak-detection/dialogs/dialog-event/services/dialog-event-persistence.service';

// history & comments tab
@Injectable()
export class DialogEventHistory {
  private readonly dialogState = inject(DialogEventState);
  private readonly persistence = inject(DialogEventPersistenceService);
  private readonly store = inject(Store);
  private readonly fb = inject(FormBuilder);

  constructor() {
    const event = this.dialogState.event();

    if (event) this.persistence.loadHistory(event.id);
  }

  /** kept here rather than in the tab, so it survives switching tabs and the Details tab can set it */
  public readonly filterForm = this.fb.nonNullable.group({ userCommentsOnly: false });

  public readonly userCommentsOnly = toSignal(this.filterForm.controls.userCommentsOnly.valueChanges, {
    initialValue: this.filterForm.controls.userCommentsOnly.value,
  });

  private readonly historyByEventId = this.store.selectSignal(selectEventsHistory);
  private readonly historyCountsByEventId = this.store.selectSignal(selectEventsHistoryCounts);

  public readonly history = computed(() => {
    const event = this.dialogState.event();

    return event ? (this.historyByEventId()[event.id] ?? []) : [];
  });

  private readonly historyCounts = computed(() => {
    const event = this.dialogState.event();

    return event ? this.historyCountsByEventId()[event.id] : undefined;
  });

  public readonly visibleHistory = computed(() => {
    const entries = this.history();

    return this.userCommentsOnly() ? entries.filter(hasUserComment) : entries;
  });

  public readonly historyCount = computed(() => {
    const counts = this.historyCounts();

    return counts ? counts.total : this.history().length;
  });

  public readonly userCommentCount = computed(() => {
    const counts = this.historyCounts();

    return counts ? counts.userComments : this.history().filter(hasUserComment).length;
  });

  public showUserCommentsOnly(): void {
    this.filterForm.controls.userCommentsOnly.setValue(true);
  }
}

function hasUserComment(entry: { comment?: string | null }): boolean {
  return !!entry.comment;
}
